/* eslint-disable complexity */
/* eslint-disable max-lines */
import { ArticleResolution, ArticleType } from '@wix/answers-api';
import { useExperiments, useHttpClient } from '@wix/fe-essentials-standalone';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useContext, useEffect, useState } from 'react';
import { DeprecatedSearchResults } from '../../components/DeprecatedSearchResults';
import { DeprecatedSearchResultsHeader } from '../../components/DeprecatedSearchResults/DeprecatedSearchResultsHeader';
import { pushUrl } from '../../components/InnerLink';
import {
  ArticleTypesCount,
  SearchResults,
} from '../../components/SearchResults';
import { NoResultsFound } from '../../components/SearchResults/NoResultsFound';
import {
  EXPERIMENTS,
  LOCALES,
  PAGES,
  SESSION_STORAGE,
  SearchResultsFilter,
} from '../../constants';
import { Context } from '../../context';
import {
  getArticleTypeCount,
  getPageSeo,
  searchArticles,
} from '../../services/answers';
import {
  ArticleSearchResult,
  ArticleSearchResultItem,
  GetWixServerSidePropsFunction,
  PageType,
} from '../../types';
import { withAppServerProps } from '../../utils/appServerProps';
import { getCategoriesPaths } from '../../utils/categories';
import { sanitize } from 'isomorphic-dompurify';
import { DeprecatedEmptySearchResults } from '../../components/SearchBar/DeprecatedEmptySearchResults';
import { useBI } from '../../hooks/useBI';
import { SearchQuestionBIParams, useSearchBarBI } from '../../components/SearchBar/bi';
import { ORIGINAL_CONTENT_ARTICLES_COUNT, kaSearchArticles } from '../../services/aiWrapper';
import { generateAnswer } from '../../services/generateAnswer';
import { LoggerNamespaces, createLogger } from '../../utils/logger';
import { isMobile, isMobileSSR, isTablet, isTabletSSR } from '../../utils/userAgent';
import { isProduction } from '@wix/wix-run-mode';
import { genAnswerMock } from '../../__mocks__/genAnswers';
import { countWords } from '../../utils/text';

export const SEARCH_RESULTS_PAGE_SIZE = 12;

const isInternalSourceFilter = (searchFilterType: SearchResultsFilter) =>
  [
    SearchResultsFilter.ARTICLES,
    SearchResultsFilter.FEATURE_REQUESTS,
    SearchResultsFilter.KNOWN_ISSUES,
  ].includes(searchFilterType);

const getFilters = (
  filterBy: SearchResultsFilter,
  isKASearchEnabled: boolean
) => {
  switch (filterBy) {
    case SearchResultsFilter.ALL:
      return {};
    case SearchResultsFilter.ARTICLES:
      return { articleType: ArticleType.ARTICLE };
    case SearchResultsFilter.KNOWN_ISSUES:
      return { articleType: ArticleType.KNOWN_ISSUE };
    case SearchResultsFilter.FEATURE_REQUESTS:
    case SearchResultsFilter.ROADMAP:
      return isKASearchEnabled
        ? { articleType: ArticleType.FEATURE_REQUEST }
        : {
          resolutions: [
            ArticleResolution.PLANNED,
            ArticleResolution.IN_DEVELOPMENT,
            ArticleResolution.COMING_SOON,
            ArticleResolution.RELEASED,
            ArticleResolution.COLLECTING_VOTES,
          ],
        };
    default:
      return {};
  }
};

type SearchPageProps = {
  searchResults: ArticleSearchResult | null;
  term: string;
  initCurrentPage: number;
  articleTypesCount?: ArticleTypesCount;
  generatedAnswer: string;
  relatedArticlesTitles: string[];
};

const itemIdKey = (term: string, page: number) => `${term}_${page}`;

const SearchPage: NextPage<SearchPageProps> = ({
  searchResults,
  articleTypesCount,
  term,
  initCurrentPage,
  generatedAnswer,
  relatedArticlesTitles,
}: SearchPageProps) => {
  const { categoriesTree, pageTemplateData, setPageTemplateData, searchSessionId } =
    useContext(Context);
  const { experiments } = useExperiments({ readOnly: true });
  const httpClient = useHttpClient();
  const [currentPage, setCurrentPage] = useState(initCurrentPage);
  const [filterBy, setFilterBy] = useState<SearchResultsFilter>(
    SearchResultsFilter.ALL
  );
  const { searchQuestionBI } = useSearchBarBI();
  const { locale } = useRouter();
  const [currentInternalSearchResults, setCurrentInternalSearchResults] =
    useState<ArticleSearchResult | null>(searchResults);
  const [isLoading, setIsLoading] = useState(false);
  const context = useContext(Context);
  const { sendBIEvent } = useBI();
  const isMobileView = isMobile(context);
  const isTabletView = isTablet(context);

  const isKASearchEnabled =
    experiments.enabled(EXPERIMENTS.SPECS.KA_SEARCH) && locale === LOCALES.EN;

  const isNextGenSearchEnabled =
    experiments.enabled(EXPERIMENTS.SPECS.NEXT_GEN_SEARCH);

  const isGenAnswerDisplayed = isNextGenSearchEnabled &&
    locale === LOCALES.EN && !isMobileView && !isTabletView &&
    generatedAnswer && generatedAnswer?.length > 0 && currentPage === 1;

  const categoryIds =
    currentInternalSearchResults?.items.map(
      (searchResult) => searchResult.categoryId
    ) || [];

  const initNumberOfResults = searchResults ? searchResults.items.length : 0;

  const deprecatedCategoryIds =
    searchResults?.items.map((searchResult) => searchResult.categoryId) || [];

  const deprecatedSearchResults = (
    <>
      <DeprecatedSearchResultsHeader
        numberOfResults={(searchResults?.items.length as number) || 0}
        term={term}
      />
      {term.length > 0 && searchResults && searchResults.items.length > 0 ? (
        <DeprecatedSearchResults
          term={term}
          searchResults={searchResults.items as ArticleSearchResultItem[]}
          categoriesPaths={getCategoriesPaths(
            categoriesTree,
            deprecatedCategoryIds
          )}
        />
      ) : (
        <DeprecatedEmptySearchResults />
      )}
    </>
  );

  useEffect(() => {
    if (searchResults) {
      setFilterBy(SearchResultsFilter.ALL);
      setCurrentPage(initCurrentPage);
      setCurrentInternalSearchResults(searchResults);
    }
  }, [searchResults, initCurrentPage, term]);

  const allPaths = getCategoriesPaths(categoriesTree, categoryIds);

  const fetchSearchResults = useCallback(
    async (page: number, newSearchFilter: SearchResultsFilter) => {
      setIsLoading(true);

      const { data } = await httpClient.get<ArticleSearchResult>(
        '/api/article/search',
        {
          params: {
            locale,
            text: term,
            pageSize: SEARCH_RESULTS_PAGE_SIZE,
            page,
            ...getFilters(newSearchFilter, isKASearchEnabled),
            ...(isKASearchEnabled ? { useKA: 'true' } : {}),
          },
        }
      );
      setIsLoading(false);
      return data;
    },
    [locale, term, isKASearchEnabled, httpClient]
  );

  const onPageChange = async (page: number) => {
    setCurrentPage(page);
    setPageTemplateData({ ...pageTemplateData, itemId: itemIdKey(term, page) });
    setCurrentInternalSearchResults(await fetchSearchResults(page, filterBy));
    await pushUrl(`${locale}/search?term=${term}&page=${page}`, true);
  };

  const setFilters = async (filter: SearchResultsFilter) => {
    setFilterBy(filter);
    setCurrentPage(1);
    const data = await fetchSearchResults(1, filter);
    setCurrentInternalSearchResults(data);
    await pushUrl(`${locale}/search?term=${term}&page=1`, true);
  };

  useEffect(() => {
    const userSearchedData = sessionStorage.getItem(
      SESSION_STORAGE.KEYS.USER_SEARCHED
    );
    if (userSearchedData) {
      const { itemId, itemType, pageType, inputType } =
        JSON.parse(userSearchedData);
      sessionStorage.removeItem(SESSION_STORAGE.KEYS.USER_SEARCHED);

      const searchQuestionBIParams: SearchQuestionBIParams = {
        results: searchResults?.items as ArticleSearchResultItem[],
        pageType,
        inputText: term,
        searchSessionId,
        searchMethod: searchResults?.searchMethod,
        searchVersion: searchResults?.searchVersion,
        itemId,
        itemType,
        inputType,
        genAnswerBasedOnArticles: getParsedArticles(),
        genAnswerWordsCount: genAnswerWordCount,
      };
      void searchQuestionBI(searchQuestionBIParams);
    }
  }, [locale, searchQuestionBI, searchResults, sendBIEvent, term, searchSessionId]);

  const genAnswerWordCount = generatedAnswer && isGenAnswerDisplayed ?
  countWords(generatedAnswer) : undefined;

  const currentSearchResults = currentInternalSearchResults;
  const FULL_CONTENT_ARTICLES_COUNT = 5;
  const getParsedArticles = () => {
    if (isGenAnswerDisplayed && currentSearchResults) {

      const topArticles = currentSearchResults.items.slice(0, FULL_CONTENT_ARTICLES_COUNT);
      const relatedArticles = topArticles
        .filter((article) => relatedArticlesTitles?.includes(article.title));
      const parsedRelatedArticles = relatedArticles.map((article) => ({
        article_id: article.id,
        output_distance: article.distance,
      }));
      return JSON.stringify(parsedRelatedArticles);
    } else { return undefined; }
  };

  const newSearchResults =
    term.length > 0 &&
      currentSearchResults &&
      initNumberOfResults &&
      initNumberOfResults > 0 ? (
      <>
        <SearchResults
          term={term}
          searchResults={currentSearchResults}
          categoriesPaths={allPaths}
          currentPage={currentPage}
          activatedTab={filterBy}
          articlesTypesCount={articleTypesCount as ArticleTypesCount}
          onPageChange={onPageChange}
          onFilterChange={setFilters}
          isLoading={isLoading}
          generatedAnswer={generatedAnswer}
          relatedArticlesTitles={relatedArticlesTitles}
        />
      </>
    ) : (
      <NoResultsFound term={term} data={searchResults} />
    );

  const isNewSearchResultsEnabled =
    experiments.enabled(
      EXPERIMENTS.SPECS.NEW_SEARCH_RESULTS_PAGE_ALL_LOCALES
    ) || locale === LOCALES.EN;

  return isNewSearchResultsEnabled ? newSearchResults : deprecatedSearchResults;
};

export const getServerSideProps: GetWixServerSidePropsFunction =
  withAppServerProps(async ({ req, res, locale, query, experiments }) => {
    const isMobileSsr = isMobileSSR(req);
    const isTabletSsr = isTabletSSR(req);
    const { term, page = 1, filter } = query;
    const isKASearchEnabled =
      experiments[EXPERIMENTS.SPECS.KA_SEARCH] === 'true' &&
      locale === LOCALES.EN;

    const isNextGenSearchEnabled = experiments[EXPERIMENTS.SPECS.NEXT_GEN_SEARCH] === 'true' &&
      locale === LOCALES.EN && !isMobileSsr && !isTabletSsr;

    const sanitizedTerm = sanitize(term as string, {
      ALLOWED_TAGS: [],
    });
    const isNewSearchResultsEnabled =
      experiments[EXPERIMENTS.SPECS.NEW_SEARCH_RESULTS_PAGE_ALL_LOCALES] ===
      'true' || locale === LOCALES.EN;

    const filterAsEnum = filter
      ? (filter as SearchResultsFilter)
      : SearchResultsFilter.ARTICLES;

    const isInternalFilter = isInternalSourceFilter(filterAsEnum);

    const filters = isInternalFilter
      ? getFilters(filterAsEnum, isKASearchEnabled)
      : null;

    let searchResults;
    if (isKASearchEnabled) {
      if (sanitizedTerm?.length === 0) {
        searchResults = {
          items: [],
          itemsCount: 0,
          searchMethod: 'KA',
          searchVersion: '1.0',
        };
      } else {
        searchResults = await kaSearchArticles(
          req.aspects,
          res.locals.appContext,
          isNextGenSearchEnabled,
          {
            query: sanitizedTerm as string,
            page: Number(page),
            pageSize: SEARCH_RESULTS_PAGE_SIZE,
          }
        );
      }
    } else {
      searchResults = await searchArticles(
        req.aspects,
        locale as string,
        {
          text: sanitizedTerm as string,
          page: isNewSearchResultsEnabled
            ? !isNaN(Number(page))
              ? Number(page)
              : undefined
            : undefined,
          pageSize: isNewSearchResultsEnabled
            ? SEARCH_RESULTS_PAGE_SIZE
            : undefined,
          types: filters?.articleType ? [filters.articleType] : undefined,
          resolutions: filters?.resolutions,
        },
        res.locals.redisClient
      );
      if (!searchResults.searchVersion) {
        searchResults.searchVersion = '';
      }
    }

    const pageSeo = await getPageSeo(
      req.aspects,
      locale,
      PageType.SearchResults,
      res.locals.redisClient
    );

    const internalArticleTypesCountRes =
      !isKASearchEnabled && isNewSearchResultsEnabled
        ? await getArticleTypeCount(
          req.aspects,
          locale as string,
          sanitizedTerm as string,
          res.locals.redisClient
        )
        : undefined;

    const initCurrentPage = Number(page) || 1;

    let generatedAnswer: string | null = null;
    let relatedArticlesTitles: string[] = [];

    if (isNextGenSearchEnabled) {
      const fiveTopArticles = searchResults.items.length > ORIGINAL_CONTENT_ARTICLES_COUNT ?
        searchResults.items.slice(0, ORIGINAL_CONTENT_ARTICLES_COUNT) : searchResults.items;

      const logger = createLogger(LoggerNamespaces.Server);

      try {
        const result = isProduction() ? await generateAnswer(
          req.aspects,
          res.locals.appContext,
          sanitizedTerm,
          fiveTopArticles
        ) : genAnswerMock;

        if (result) {
          generatedAnswer = result.solution;
          relatedArticlesTitles = result.used_articles;
        }

      } catch (error) {
        logger
          .withRequest(req)
          .error(
            `Error getting answer from service generateAnswer, search term: ${sanitizedTerm} , error: ${error}`,
          );
      }
    }

    return {
      props: {
        term: sanitizedTerm,
        pageType: PAGES.SEARCH_RESULTS,
        pageData: { searchResults, term: sanitizedTerm },
        searchResults: isInternalFilter ? searchResults : null,
        articleTypesCount: isKASearchEnabled
          ? {
            [ArticleType.ARTICLE]:
              (searchResults.itemsCountPerType &&
                searchResults.itemsCountPerType[ArticleType.ARTICLE]) ||
              0,
            [ArticleType.KNOWN_ISSUE]:
              (searchResults.itemsCountPerType &&
                searchResults.itemsCountPerType[ArticleType.KNOWN_ISSUE]) ||
              0,
            [ArticleType.FEATURE_REQUEST]:
              (searchResults.itemsCountPerType &&
                searchResults.itemsCountPerType[
                ArticleType.FEATURE_REQUEST
                ]) ||
              0,
          }
          : internalArticleTypesCountRes
            ? {
              [ArticleType.ARTICLE]: 0,
              [ArticleType.KNOWN_ISSUE]: 0,
              [ArticleType.FEATURE_REQUEST]: 0,
              ...internalArticleTypesCountRes.articleTypeCount,
            }
            : null,
        initCurrentPage,
        filterType: filterAsEnum,
        itemId: itemIdKey(sanitizedTerm, initCurrentPage),
        pageSeo,
        generatedAnswer: generatedAnswer || null,
        relatedArticlesTitles: relatedArticlesTitles || null,
      },
    };
  });

export default SearchPage;
